import { useMutation, UseMutationOptions, useQuery, useQueryClient } from 'react-query';

import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import type { APIList, StringifiedUUID } from 'types';
import { SourceType } from 'types/index';
import { SPMColumnCreateRequest, SPMWorksheet, SPMWorksheetRosterMember } from 'types/SPM';
import { APIError } from 'utils/errors';
import { useCallback, useMemo } from 'react';
import { QueryHookOptions } from 'types/ReactQuery';

export type SPMCreatePlanningCycleWorksheetRequest = {
  name: string;
  description?: string;
  unique_id_column_name: string;
  worksheet_type: string;
  time_period_column_spec: SPMTimePeriodColumnSpec;
  columns?: SPMColumnCreateRequest[];
  property_filters?: Record<string, string[]>;
  property_worksheet_ids?: string[];
};

type SPMTimePeriodColumnSpec = {
  variant: string;
  data_type?: string;
  format?: Record<string, any>;
};
export type SPMRosterAttributeSpec = {
  source_roster_worksheet_id: string;
  source_roster_worksheet_column_ids: string[];
};

export type SPMCreateModelWorksheetRequest = {
  name: string;
  description?: string;
  unique_id_column_name: string;
  worksheet_type: string;
  tab_id?: StringifiedUUID;
  is_output?: boolean;
  include_time_period_columns: boolean;
  time_period_column_spec: SPMTimePeriodColumnSpec;
  roster_attribute_spec?: SPMRosterAttributeSpec;
  property_worksheet_ids?: string[];
  columns?: SPMColumnCreateRequest[];
  property_filters?: Record<string, string[]>;
};

export type SPMUpdateColumnOrderRequest = {
  column_order: StringifiedUUID[];
};

export type SPMCloneWorksheetRequest = {
  name: string;
  worksheetId?: StringifiedUUID;
  description?: string;
  tab_id?: StringifiedUUID;
};

export type SPMUpdateWorksheetRequest = {
  worksheetId: StringifiedUUID;
  name: string;
  description?: string;
  include_time_period_columns: boolean;
  time_period_column_spec?: SPMTimePeriodColumnSpec;
  columns_width?: Record<StringifiedUUID, number>;
  roster_attribute_spec?: SPMRosterAttributeSpec;
  property_filters?: Record<string, string[]>;
  property_worksheet_ids?: string[];
};

export type SPMConnectWorksheetVersionedCopyRequest = {
  source_type: SourceType;
  source_id: string;
  column_mapping?: Record<string, any>;
  sync_mode?: string;
  unique_column_id?: StringifiedUUID;
};

export type SPMCreateWorksheetVersionedCopyRequest = SPMConnectWorksheetVersionedCopyRequest & {
  name: string;
  description?: string;
};

export type SPMSyncVersionedCopyResponse = {
  object: 'sync_task';
  task_id?: string;
};

export type SPMCsvExportResponse = {
  aggregation_id: string;
};

// Base parameter type for API calls
export type SPMWorksheetParams = {
  planningCycleId: StringifiedUUID;
  spmModelId?: StringifiedUUID;
  scenarioId?: StringifiedUUID;
};

export type SPMWorksheetIdentifierParams = {
  planningCycleId: StringifiedUUID;
  worksheetId: StringifiedUUID;
  spmModelId?: StringifiedUUID;
};

export type SPMWorksheetMutationParams = {
  csrfToken: string;
  planningCycleId: StringifiedUUID;
  spmModelId?: StringifiedUUID;
};

export type SPMWorksheetRosterParams = {
  planningCycleId: StringifiedUUID;
  spmModelId: StringifiedUUID;
  worksheetId: StringifiedUUID;
  scenarioId?: StringifiedUUID;
  enrolledOnly?: boolean;
};

export type SPMWorksheetDeleteParams = {
  csrfToken: string;
  planningCycleId: StringifiedUUID;
  spmModelId?: StringifiedUUID;
};

// Unified API URL builder
const apiUrl = ({ planningCycleId, spmModelId }: SPMWorksheetParams) => {
  if (spmModelId) {
    return `/api/v1/spm/planning-cycles/${planningCycleId}/spm-models/${spmModelId}/worksheets/`;
  }
  return `/api/v1/spm/planning-cycles/${planningCycleId}/worksheets/`;
};

const listSPMWorksheets = (params: SPMWorksheetParams) => {
  const qs = params.scenarioId ? `?scenario_id=${encodeURIComponent(params.scenarioId)}` : '';
  return getRequest<null, APIList<SPMWorksheet>>(`${apiUrl(params)}${qs}`);
};

const getSPMWorksheetQueryKey = (
  params: Pick<SPMWorksheetParams, 'planningCycleId' | 'spmModelId'>,
  worksheetId: string,
) =>
  params.spmModelId
    ? ['spm', 'planning-cycles', params.planningCycleId, 'spm-models', params.spmModelId, 'worksheets', worksheetId] // todo: check scenario here
    : ['spm', 'planning-cycles', params.planningCycleId, 'worksheets', worksheetId];

const getListSPMWorksheetsQueryKey = (params: SPMWorksheetParams) => {
  const base = params.spmModelId
    ? ['spm', 'planning-cycles', params.planningCycleId, 'spm-models', params.spmModelId, 'worksheets']
    : ['spm', 'planning-cycles', params.planningCycleId, 'worksheets'];
  return params.scenarioId ? [...base, 'scenario', params.scenarioId] : base;
};

const useListSPMWorksheetsQuery = (
  params: SPMWorksheetParams,
  options: QueryHookOptions<APIList<SPMWorksheet>, APIError> = {},
) => {
  const QUERY_KEY = getListSPMWorksheetsQueryKey(params);
  const queryClient = useQueryClient();

  const query = useQuery<APIList<SPMWorksheet>, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => listSPMWorksheets(params),
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery, isLoading: query.isLoading };
};

// Get single worksheet
const getSPMWorksheet = (params: SPMWorksheetIdentifierParams) =>
  getRequest<null, SPMWorksheet>(`${apiUrl(params)}${params.worksheetId}/`);

const useGetSPMWorksheetQuery = (
  params: SPMWorksheetIdentifierParams,
  options: QueryHookOptions<SPMWorksheet, APIError> = {},
) => {
  const { planningCycleId, worksheetId, spmModelId } = params;
  const QUERY_KEY = useMemo(
    () => getSPMWorksheetQueryKey({ planningCycleId, spmModelId }, worksheetId),
    [planningCycleId, spmModelId, worksheetId],
  );
  const queryClient = useQueryClient();

  const query = useQuery<SPMWorksheet, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getSPMWorksheet(params),
  });
  const invalidateQuery = useCallback(
    () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    [QUERY_KEY, queryClient],
  );

  return { query, invalidateQuery };
};

// Get worksheet roster (model level only)
const getWorksheetRoster = (params: SPMWorksheetRosterParams) => {
  const { planningCycleId, spmModelId, worksheetId, scenarioId, enrolledOnly } = params;
  const url = `${apiUrl({ planningCycleId, spmModelId })}${worksheetId}/roster_members/`;
  const queryParams = new URLSearchParams();
  if (scenarioId) queryParams.set('scenario_id', scenarioId);
  if (enrolledOnly) queryParams.set('enrolled_only', 'true');
  const queryString = queryParams.toString();
  return getRequest<null, APIList<SPMWorksheetRosterMember>>(queryString ? `${url}?${queryString}` : url);
};

const useGetWorksheetRosterQuery = (
  params: SPMWorksheetRosterParams,
  options: QueryHookOptions<APIList<SPMWorksheetRosterMember>, APIError> = {},
) => {
  const { planningCycleId, spmModelId, worksheetId, scenarioId, enrolledOnly } = params;
  const QUERY_KEY = [
    'spm',
    'planning-cycles',
    planningCycleId,
    'spm-models',
    spmModelId,
    'worksheets',
    worksheetId,
    'scenario',
    scenarioId,
    'roster',
    ...(enrolledOnly ? ['enrolled-only'] : []),
  ];
  const queryClient = useQueryClient();

  const query = useQuery<APIList<SPMWorksheetRosterMember>, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getWorksheetRoster(params),
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

// Create worksheet (both planning cycle and model level)
const createWorksheet =
  (params: SPMWorksheetMutationParams) =>
  (request: SPMCreatePlanningCycleWorksheetRequest | SPMCreateModelWorksheetRequest) =>
    postRequest<SPMCreatePlanningCycleWorksheetRequest | SPMCreateModelWorksheetRequest, SPMWorksheet>(
      `${apiUrl(params)}`,
      request,
      params.csrfToken,
    );

const useCreateSPMWorksheetMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<
    SPMWorksheet,
    APIError,
    SPMCreatePlanningCycleWorksheetRequest | SPMCreateModelWorksheetRequest
  > = {},
) => {
  const { planningCycleId, spmModelId } = params;
  const queryClient = useQueryClient();
  const QUERY_KEY = spmModelId
    ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets'] // todo: check scenario here
    : ['spm', 'planning-cycles', planningCycleId, 'worksheets'];

  return {
    mutation: useMutation<
      SPMWorksheet,
      APIError,
      SPMCreatePlanningCycleWorksheetRequest | SPMCreateModelWorksheetRequest
    >({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        // Invalidate the list query to refetch SPM worksheets
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        queryClient.invalidateQueries({
          queryKey: ['spm', 'planning-cycles', planningCycleId, 'connected-modeling-worksheets'],
        });

        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: createWorksheet(params),
    }),
  };
};

const updateSPMWorksheet = (params: SPMWorksheetMutationParams) => (request: SPMUpdateWorksheetRequest) =>
  putRequest<Omit<SPMUpdateWorksheetRequest, 'worksheetId'>, SPMWorksheet>(
    `${apiUrl(params)}${request.worksheetId}/`,
    {
      name: request.name,
      description: request.description,
      include_time_period_columns: request.include_time_period_columns,
      time_period_column_spec: request.time_period_column_spec,
      columns_width: request.columns_width,
      roster_attribute_spec: request.roster_attribute_spec,
      property_filters: request.property_filters,
      ...(request.property_worksheet_ids != null && request.property_worksheet_ids.length > 0
        ? { property_worksheet_ids: request.property_worksheet_ids }
        : {}),
    },
    params.csrfToken,
  );

const useUpdateSPMWorksheetMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<SPMWorksheet, APIError, SPMUpdateWorksheetRequest> = {},
) => {
  const { planningCycleId } = params;
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<SPMWorksheet, APIError, SPMUpdateWorksheetRequest>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        queryClient.invalidateQueries({ queryKey: ['spm', 'planning-cycles', planningCycleId] });
        // Invalidate query for connected modelling worksheets if it's a planning cycle level worksheet
        if (!params.spmModelId) {
          queryClient.invalidateQueries({
            queryKey: ['planning-cycles', planningCycleId, 'connected-modeling-worksheets'],
          });
        }
        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: updateSPMWorksheet(params),
    }),
  };
};

type SPMUpdateWorksheetColumnsWidthRequest = {
  worksheetId: string;
  columnsWidth: Record<StringifiedUUID, number>;
};

type SPMUpdateWorksheetColumnsWidthContext = { previousWorksheet?: SPMWorksheet };

const useUpdateSPMWorksheetColumnsWidthMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<SPMWorksheet, APIError, SPMUpdateWorksheetColumnsWidthRequest> = {},
) => {
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<
      SPMWorksheet,
      APIError,
      SPMUpdateWorksheetColumnsWidthRequest,
      SPMUpdateWorksheetColumnsWidthContext
    >({
      ...options,
      onMutate: async (request, mutationContext) => {
        await options.onMutate?.(request, mutationContext);
        const queryKey = getSPMWorksheetQueryKey(params, request.worksheetId);
        await queryClient.cancelQueries({ queryKey, exact: true });
        const previousWorksheet = queryClient.getQueryData<SPMWorksheet>(queryKey);
        if (previousWorksheet) {
          queryClient.setQueryData<SPMWorksheet>(queryKey, {
            ...previousWorksheet,
            columns_width: request.columnsWidth,
          });
        }
        return { previousWorksheet };
      },
      onError: (error, request, callbackContext, mutationContext) => {
        if (callbackContext?.previousWorksheet) {
          queryClient.setQueryData(
            getSPMWorksheetQueryKey(params, request.worksheetId),
            callbackContext.previousWorksheet,
          );
        }
        options.onError?.(error, request, callbackContext, mutationContext);
      },
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        queryClient.invalidateQueries({ queryKey: getSPMWorksheetQueryKey(params, data.id) });
        options.onSuccess?.(data, variables, callbackContext, mutationContext);
      },
      mutationFn: (request) =>
        putRequest<{ columns_width: Record<StringifiedUUID, number> }, SPMWorksheet>(
          `${apiUrl(params)}${request.worksheetId}/`,
          { columns_width: request.columnsWidth },
          params.csrfToken,
        ),
    }),
  };
};

type SPMUpdateWorksheetColumnTotalsAggregationsRequest = {
  worksheetId: string;
  columnTotalsAggregations: Record<StringifiedUUID, string>;
};

const useUpdateSPMWorksheetColumnTotalsAggregationsMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<SPMWorksheet, APIError, SPMUpdateWorksheetColumnTotalsAggregationsRequest> = {},
) => {
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<SPMWorksheet, APIError, SPMUpdateWorksheetColumnTotalsAggregationsRequest>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        const queryKey = params.spmModelId
          ? ['spm', 'planning-cycles', params.planningCycleId, 'spm-models', params.spmModelId, 'worksheets', data.id]
          : ['spm', 'planning-cycles', params.planningCycleId, 'worksheets', data.id];
        queryClient.invalidateQueries({ queryKey });
        options.onSuccess?.(data, variables, callbackContext, mutationContext);
      },
      mutationFn: (request) =>
        putRequest<{ column_totals_aggregations: Record<StringifiedUUID, string> }, SPMWorksheet>(
          `${apiUrl(params)}${request.worksheetId}/`,
          { column_totals_aggregations: request.columnTotalsAggregations },
          params.csrfToken,
        ),
    }),
  };
};

type SPMUpdateWorksheetShowColumnTotalsRequest = {
  worksheetId: string;
  showColumnTotals: boolean;
};

const useUpdateSPMWorksheetShowColumnTotalsMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<SPMWorksheet, APIError, SPMUpdateWorksheetShowColumnTotalsRequest> = {},
) => {
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<SPMWorksheet, APIError, SPMUpdateWorksheetShowColumnTotalsRequest>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        const queryKey = params.spmModelId
          ? ['spm', 'planning-cycles', params.planningCycleId, 'spm-models', params.spmModelId, 'worksheets', data.id]
          : ['spm', 'planning-cycles', params.planningCycleId, 'worksheets', data.id];
        queryClient.invalidateQueries({ queryKey });
        options.onSuccess?.(data, variables, callbackContext, mutationContext);
      },
      mutationFn: (request) =>
        putRequest<{ show_column_totals: boolean }, SPMWorksheet>(
          `${apiUrl(params)}${request.worksheetId}/`,
          { show_column_totals: request.showColumnTotals },
          params.csrfToken,
        ),
    }),
  };
};

// Create versioned copy worksheet (both planning cycle and model level)
const createVersionedCopyWorksheet =
  (params: SPMWorksheetMutationParams) => (request: SPMCreateWorksheetVersionedCopyRequest) =>
    postRequest<SPMCreateWorksheetVersionedCopyRequest, SPMWorksheet>(
      `${apiUrl(params)}create-worksheet-with-versioned-copy/`,
      request,
      params.csrfToken,
    );

const useCreateVersionedCopyWorksheetMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<SPMWorksheet, APIError, SPMCreateWorksheetVersionedCopyRequest> = {},
) => {
  const { planningCycleId, spmModelId } = params;
  const queryClient = useQueryClient();
  const QUERY_KEY = spmModelId
    ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets']
    : ['spm', 'planning-cycles', planningCycleId, 'worksheets'];

  return {
    mutation: useMutation<SPMWorksheet, APIError, SPMCreateWorksheetVersionedCopyRequest>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        // Invalidate the list query to refetch SPM worksheets
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        queryClient.invalidateQueries({
          queryKey: ['spm', 'planning-cycles', planningCycleId, 'connected-modeling-worksheets'],
        });

        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: createVersionedCopyWorksheet(params),
    }),
  };
};

// Connect existing worksheet to versioned copy source (both planning cycle and model level)
const connectWorksheetVersionedCopy =
  (params: SPMWorksheetMutationParams) =>
  (worksheetId: StringifiedUUID, request: SPMConnectWorksheetVersionedCopyRequest) =>
    postRequest<SPMConnectWorksheetVersionedCopyRequest, SPMWorksheet>(
      `${apiUrl(params)}${worksheetId}/versioned-copy/`,
      request,
      params.csrfToken,
    );

const useConnectWorksheetVersionedCopyMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<
    SPMWorksheet,
    APIError,
    { worksheetId: StringifiedUUID; request: SPMConnectWorksheetVersionedCopyRequest }
  > = {},
) => {
  const { planningCycleId, spmModelId } = params;
  const queryClient = useQueryClient();
  const QUERY_KEY = spmModelId
    ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets']
    : ['spm', 'planning-cycles', planningCycleId, 'worksheets'];

  return {
    mutation: useMutation<
      SPMWorksheet,
      APIError,
      { worksheetId: StringifiedUUID; request: SPMConnectWorksheetVersionedCopyRequest }
    >({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        // Invalidate the list query to refetch SPM worksheets
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        // Also invalidate the specific worksheet query
        const worksheetQueryKey = spmModelId
          ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets', variables.worksheetId]
          : ['spm', 'planning-cycles', planningCycleId, 'worksheets', variables.worksheetId];
        queryClient.invalidateQueries({ queryKey: worksheetQueryKey });
        queryClient.invalidateQueries({
          queryKey: ['spm', 'planning-cycles', planningCycleId, 'connected-modeling-worksheets'],
        });

        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: ({ worksheetId, request }) => connectWorksheetVersionedCopy(params)(worksheetId, request),
    }),
  };
};

// Sync versioned copy worksheet (both planning cycle and model level)
const syncVersionedCopyWorksheet = (params: SPMWorksheetMutationParams) => (worksheetId: StringifiedUUID) =>
  postRequest<null, SPMSyncVersionedCopyResponse>(
    `${apiUrl(params)}${worksheetId}/sync-versioned-copy/`,
    null,
    params.csrfToken,
  );

const useSyncVersionedCopyWorksheetMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<SPMSyncVersionedCopyResponse, APIError, StringifiedUUID> = {},
) => {
  const { planningCycleId, spmModelId } = params;
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<SPMSyncVersionedCopyResponse, APIError, StringifiedUUID>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        // Invalidate only the specific worksheet query — NOT the list.
        // The list invalidation triggers `usePrefetchRecords`'s cache subscription
        // which fires a premature prefetch while the sync task is still running,
        // poisoning the prefetch token with an empty S3 map.
        // The list is refreshed when sync completes via `handleSyncCompletion`.
        const worksheetQueryKey = spmModelId
          ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets', variables]
          : ['spm', 'planning-cycles', planningCycleId, 'worksheets', variables];
        queryClient.invalidateQueries({ queryKey: worksheetQueryKey });

        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: syncVersionedCopyWorksheet(params),
    }),
  };
};

// Export worksheet to CSV
const exportWorksheetCsv =
  (params: SPMWorksheetMutationParams & { scenarioId?: StringifiedUUID }) => (worksheetId: StringifiedUUID) => {
    const { scenarioId } = params;
    const url = `${apiUrl(params)}${worksheetId}/csv_export/${scenarioId ? `?scenario_uuid=${scenarioId}` : ''}`;
    return getRequest<null, SPMCsvExportResponse>(url);
  };

const useExportSPMWorksheetCsvMutation = (
  params: SPMWorksheetMutationParams & { scenarioId?: StringifiedUUID },
  options: UseMutationOptions<SPMCsvExportResponse, APIError, StringifiedUUID> = {},
) => {
  return {
    mutation: useMutation<SPMCsvExportResponse, APIError, StringifiedUUID>({
      ...options,
      mutationFn: exportWorksheetCsv(params),
    }),
  };
};

// Clone worksheet
const cloneSPMWorksheet =
  (params: SPMWorksheetMutationParams) => (worksheetId: StringifiedUUID, payload: SPMCloneWorksheetRequest) =>
    postRequest<SPMCloneWorksheetRequest, SPMWorksheet>(
      `${apiUrl(params)}${worksheetId}/clone/`,
      {
        name: payload.name,
        description: payload.description,
        tab_id: payload.tab_id,
      },
      params.csrfToken,
    );

const useCloneSPMWorksheetMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<SPMWorksheet, APIError, SPMCloneWorksheetRequest> = {},
) => {
  const { planningCycleId, spmModelId } = params;
  const queryClient = useQueryClient();
  const QUERY_KEY = spmModelId
    ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets']
    : ['spm', 'planning-cycles', planningCycleId, 'worksheets'];

  return {
    mutation: useMutation<SPMWorksheet, APIError, SPMCloneWorksheetRequest>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        // Invalidate the list query to refetch SPM worksheets
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        queryClient.invalidateQueries({
          queryKey: ['spm', 'planning-cycles', planningCycleId, 'connected-modeling-worksheets'],
        });

        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: (request) => cloneSPMWorksheet(params)(request.worksheetId || '', request),
    }),
  };
};

// Disconnect worksheet from versioned copy source (both planning cycle and model level)
const disconnectWorksheetVersionedCopy = (params: SPMWorksheetMutationParams) => (worksheetId: StringifiedUUID) =>
  deleteRequest<null, SPMWorksheet>(
    `${apiUrl(params)}${worksheetId}/disconnect-versioned-copy/`,
    null,
    params.csrfToken,
  );

const useDisconnectWorksheetVersionedCopyMutation = (
  params: SPMWorksheetMutationParams,
  options: UseMutationOptions<SPMWorksheet, APIError, StringifiedUUID> = {},
) => {
  const { planningCycleId, spmModelId } = params;
  const queryClient = useQueryClient();
  const QUERY_KEY = spmModelId
    ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets']
    : ['spm', 'planning-cycles', planningCycleId, 'worksheets'];

  return {
    mutation: useMutation<SPMWorksheet, APIError, StringifiedUUID>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        // Invalidate the list query to refetch SPM worksheets after disconnect
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        // Also invalidate the specific worksheet query
        const worksheetQueryKey = spmModelId
          ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets', variables]
          : ['spm', 'planning-cycles', planningCycleId, 'worksheets', variables];
        queryClient.invalidateQueries({ queryKey: worksheetQueryKey });
        queryClient.invalidateQueries({
          queryKey: ['spm', 'planning-cycles', planningCycleId, 'connected-modeling-worksheets'],
        });

        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: disconnectWorksheetVersionedCopy(params),
    }),
  };
};

// Delete worksheet (model level only)
const deleteSPMWorksheet = (params: SPMWorksheetDeleteParams) => (worksheetId: StringifiedUUID) => {
  return deleteRequest<null, void>(`${apiUrl(params)}${worksheetId}/`, null, params.csrfToken);
};

const useDeleteSPMWorksheetMutation = (
  params: SPMWorksheetDeleteParams,
  options: UseMutationOptions<void, APIError, StringifiedUUID> = {},
) => {
  const { planningCycleId, spmModelId } = params;
  const queryClient = useQueryClient();
  const QUERY_KEY = spmModelId
    ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets'] // todo: check scenario here
    : ['spm', 'planning-cycles', planningCycleId, 'worksheets'];

  return {
    mutation: useMutation<void, APIError, StringifiedUUID>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: deleteSPMWorksheet(params),
    }),
  };
};

// Update column order
const updateSPMWorksheetColumnOrder =
  (params: SPMWorksheetIdentifierParams & { csrfToken: string }) => (request: SPMUpdateColumnOrderRequest) => {
    const { planningCycleId, spmModelId, worksheetId, csrfToken } = params;
    const url = `${apiUrl({ planningCycleId, spmModelId })}${worksheetId}/column-order/`;
    return putRequest<SPMUpdateColumnOrderRequest, SPMWorksheet>(url, request, csrfToken);
  };

const useUpdateSPMWorksheetColumnOrderMutation = (
  params: SPMWorksheetIdentifierParams & { csrfToken: string },
  options: UseMutationOptions<SPMWorksheet, APIError, SPMUpdateColumnOrderRequest> = {},
) => {
  const { planningCycleId, spmModelId, worksheetId } = params;
  const queryClient = useQueryClient();

  const WORKSHEET_QUERY_KEY = spmModelId
    ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets', worksheetId]
    : ['spm', 'planning-cycles', planningCycleId, 'worksheets', worksheetId];

  const WORKSHEETS_LIST_QUERY_KEY = spmModelId
    ? ['spm', 'planning-cycles', planningCycleId, 'spm-models', spmModelId, 'worksheets']
    : ['spm', 'planning-cycles', planningCycleId, 'worksheets'];

  return {
    mutation: useMutation<SPMWorksheet, APIError, SPMUpdateColumnOrderRequest>({
      ...options,
      onSuccess: (data, variables, callbackContext, mutationContext) => {
        queryClient.invalidateQueries({ queryKey: WORKSHEET_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: WORKSHEETS_LIST_QUERY_KEY });
        if (options.onSuccess) {
          options.onSuccess(data, variables, callbackContext, mutationContext);
        }
      },
      mutationFn: updateSPMWorksheetColumnOrder(params),
    }),
  };
};

export const SPMWorksheetService = {
  apiUrl,
  getListSPMWorksheetsQueryKey,
  useListSPMWorksheetsQuery,
  useGetSPMWorksheetQuery,
  useGetWorksheetRosterQuery,
  useCreateSPMWorksheetMutation,
  useUpdateSPMWorksheetMutation,
  useCreateVersionedCopyWorksheetMutation,
  useConnectWorksheetVersionedCopyMutation,
  useDisconnectWorksheetVersionedCopyMutation,
  useSyncVersionedCopyWorksheetMutation,
  useExportSPMWorksheetCsvMutation,
  useDeleteSPMWorksheetMutation,
  useCloneSPMWorksheetMutation,
  useUpdateSPMWorksheetColumnOrderMutation,
  useUpdateSPMWorksheetColumnsWidthMutation,
  useUpdateSPMWorksheetShowColumnTotalsMutation,
  useUpdateSPMWorksheetColumnTotalsAggregationsMutation,
};
